"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { AlertIcon, CheckIcon, CloseIcon, InfoIcon } from "./icons.tsx";
import { useMounted } from "./hooks.ts";

/* ──────────────────────────────────────────────────────────────────────────
   Toasts.

   Two things they must do that a naive implementation does not:

   an undo has to outlive the toast that offered it — the action is held on the
   toast object rather than in a closure that a re-render replaces; and

   a failure never auto-dismisses. Something that went wrong stays on screen
   until somebody acknowledges it, because a five-second error is an error
   nobody saw.
   ────────────────────────────────────────────────────────────────────────── */

export type ToastTone = "good" | "bad" | "info";

export type Toast = {
  id: string;
  tone: ToastTone;
  title: string;
  body?: string;
  /** Milliseconds. Failures ignore this and stay. */
  duration?: number;
  action?: { label: string; onSelect: () => void };
};

type ToastInput = Omit<Toast, "id">;

type ToastApi = {
  push: (toast: ToastInput) => string;
  dismiss: (id: string) => void;
  success: (title: string, body?: string, action?: Toast["action"]) => string;
  error: (title: string, body?: string) => string;
  info: (title: string, body?: string, action?: Toast["action"]) => string;
};

const Context = createContext<ToastApi | null>(null);

let sequence = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [leaving, setLeaving] = useState<Set<string>>(new Set());
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const mounted = useMounted();

  const dismiss = useCallback((id: string) => {
    setLeaving((current) => new Set(current).add(id));
    const timer = timers.current.get(id);
    if (timer) clearTimeout(timer);
    timers.current.delete(id);
    // Removed after the exit animation, so it does not vanish mid-slide.
    setTimeout(() => {
      setToasts((current) => current.filter((toast) => toast.id !== id));
      setLeaving((current) => {
        const next = new Set(current);
        next.delete(id);
        return next;
      });
    }, 180);
  }, []);

  const push = useCallback(
    (input: ToastInput) => {
      sequence += 1;
      const id = `t${sequence}`;
      const toast: Toast = { ...input, id };
      setToasts((current) => [...current.slice(-4), toast]);
      const duration = toast.tone === "bad" ? 0 : (toast.duration ?? (toast.action ? 8000 : 3600));
      if (duration > 0) {
        timers.current.set(
          id,
          setTimeout(() => dismiss(id), duration)
        );
      }
      return id;
    },
    [dismiss]
  );

  useEffect(() => {
    const map = timers.current;
    return () => {
      for (const timer of map.values()) clearTimeout(timer);
      map.clear();
    };
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      push,
      dismiss,
      success: (title, body, action) => push({ tone: "good", title, body, action }),
      error: (title, body) => push({ tone: "bad", title, body }),
      info: (title, body, action) => push({ tone: "info", title, body, action }),
    }),
    [push, dismiss]
  );

  return (
    <Context.Provider value={api}>
      {children}
      {mounted
        ? createPortal(
            <div className="toast-region" role="region" aria-label="Notifications">
              {toasts.map((toast) => (
                <div
                  key={toast.id}
                  className={`toast ${toast.tone}${leaving.has(toast.id) ? " leaving" : ""}`}
                  role={toast.tone === "bad" ? "alert" : "status"}
                  aria-live={toast.tone === "bad" ? "assertive" : "polite"}
                >
                  <span className="toast-mark">
                    {toast.tone === "good" ? (
                      <CheckIcon size={14} />
                    ) : toast.tone === "bad" ? (
                      <AlertIcon size={14} />
                    ) : (
                      <InfoIcon size={14} />
                    )}
                  </span>
                  <span className="toast-copy">
                    <strong>{toast.title}</strong>
                    {toast.body ? <small>{toast.body}</small> : null}
                  </span>
                  <span className="toast-action">
                    {toast.action ? (
                      <button
                        type="button"
                        className="secondary-button tiny"
                        onClick={() => {
                          toast.action?.onSelect();
                          dismiss(toast.id);
                        }}
                      >
                        {toast.action.label}
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="icon-button ghost small"
                      onClick={() => dismiss(toast.id)}
                      aria-label="Dismiss"
                    >
                      <CloseIcon size={12} />
                    </button>
                  </span>
                </div>
              ))}
            </div>,
            document.body
          )
        : null}
    </Context.Provider>
  );
}

export function useToast(): ToastApi {
  const api = useContext(Context);
  if (!api) {
    throw new Error("useToast must be used inside a ToastProvider.");
  }
  return api;
}
